import React, { useState, useEffect, useMemo } from 'react';
import { ReptoolModal } from '../utilities/ReptoolModal.tsx'
import { Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { CheckoutForm } from './SubscriptionForm.tsx';
import { Carousel } from 'react-responsive-carousel';
import { SelectSubscriptionPrice } from './SelectSubscriptionPrice.tsx';
import { SubscriptionType, SubscriptionValue } from './types/SubscriptionTypes.ts';
import { formatIntegerCentAmount } from '../utilities/TextFormatters.ts';
import { ResendVerificationEmailForm } from '../utilities/ResendVerificationEmail.tsx';
import { Elements } from "@stripe/react-stripe-js";
import { UserInfo } from '../../types/UserInfo.ts';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { useRepToolContext } from '../../RepToolContext.tsx';
// create a modal that displays the 3 different subscription options
// and allows the user to select one to purchase Free, Basic, Premium



export const Features = [
  "Genetic Calculator",
  "Collection Management",
  "Pedigree Management",
  "Weight Tracking",
  "Care Records",
  "Care Sheets",
  "Browsing Morphs",
  "Clutch Management",
  "Pairing Tracker",
  "Collection Statistics",
  "Share Available Reptiles",
]

const SubscriptionFeatures = {
  Free: {
    name: "Free",
    value: "FREE",
    minPrice: 0,
    maxPrice: 0,
    defaultPrice: 0,
    numberOfReptiles: 50,
    features: [
      "Genetic Calculator",
      "Collection Management",
      "Pedigree Management",
      "Care Records",
      "Weight Tracking",
      "Browsing Morphs",
      "Care Sheets",
    ]
  },
  Basic: {
    name: "Basic",
    value: "BASIC",
    minPrice: 100,
    maxPrice: 600,
    defaultPrice: 500,
    numberOfReptiles: 1000,
    features: [
      "Genetic Calculator",
      "Collection Management",
      "Pedigree Management",
      "Weight Tracking",
      "Care Records",
      "Care Sheets",
      "Clutch Management",
      "Pairing Tracker",
      "Collection Statistics",
      "Browsing Morphs",
      "Share Available Reptiles",
    ]
  },
  Premium: {
    name: "Premium",
    value: "PREMIUM",
    minPrice: 600,
    maxPrice: 5000,
    defaultPrice: 2500,
    numberOfReptiles: 1000000,
    features: [...Features]
  }
}

export const FeatureRequiresSubscription = ({ clickFunction }: {
  clickFunction: () => void
}) => {
  return (
    <div>
      <h4>Feature Requires Subscription</h4>
      <p>You must have a subscription to use this feature. Would you like to set one up now?</p>
      <Button variant='outline-light' onClick={clickFunction}>Continue</Button>
    </div>
  )

}


const SubscriptionFeature = ({
  feature,
  setColumnHover,
  featureGroup,
  columnNumber,
  borderStyle,
}: {
  feature: string;
  setColumnHover: (hover: number | null) => void;
  featureGroup: keyof typeof SubscriptionFeatures;
  columnNumber: number;
  borderStyle?: string;
}) => {
  return (
    <td
      onMouseEnter={() => setColumnHover(columnNumber)}
      onMouseLeave={() => setColumnHover(null)}
      className='text-center'
      style={{
        borderLeft: borderStyle,
        borderRight: borderStyle,
      }}
    >
      {SubscriptionFeatures[featureGroup].features.includes(feature) ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faTimes} />}
    </td>
  )
}

export const SubscriptionTable = ({
  onSelect,
  currentPlan,
}: {
  onSelect: (subscription: SubscriptionType) => void;
  currentPlan?: SubscriptionValue;
}) => {
  const [columnHover, setColumnHover] = React.useState<number | null>(null);
  const currentHover = useMemo(() => {
    if (!currentPlan) return null;
    if (currentPlan === 'FREE') return 1;
    if (currentPlan === 'BASIC') return 2;
    if (currentPlan === 'PREMIUM') return 3;
    return null;
  }, [currentPlan]);

  const borderStyle = (columnHover: number | null, currentHover: number | null, columnNumber: number) => {
    if (columnHover === columnNumber) return '1px solid white';
    if (currentHover === columnNumber) return '1px solid #999';
    return '';
  }

  return <Table variant='dark' size='sm' striped>
    <thead>
      <tr>
        <th>Features</th>
        <th
          className='text-center'
          onMouseEnter={() => setColumnHover(1)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 1),
            borderLeft: borderStyle(columnHover, currentHover, 1),
            borderTop: borderStyle(columnHover, currentHover, 1),
          }}
        >
          Free
          {currentPlan === 'FREE' && <div><small className='mt-2'>(Current Plan)</small></div>}
        </th>
        <th
          className='text-center'
          onMouseEnter={() => setColumnHover(2)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 2),
            borderLeft: borderStyle(columnHover, currentHover, 2),
            borderTop: borderStyle(columnHover, currentHover, 2),
          }}
        >
          Basic
          {currentPlan === 'BASIC' && <div><small className='mt-2'>(Current Plan)</small></div>}
        </th>
        <th
          className='text-center'
          onMouseEnter={() => setColumnHover(3)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 3),
            borderLeft: borderStyle(columnHover, currentHover, 3),
            borderTop: borderStyle(columnHover, currentHover, 3),
          }}
        >

          Premium
          {currentPlan === 'PREMIUM' && <div><small className='mt-2'>(Current Plan)</small></div>}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Max Number of Reptiles</td>
        <td
          className='text-center'
          onMouseEnter={() => setColumnHover(1)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 1),
            borderLeft: borderStyle(columnHover, currentHover, 1),
          }}
        >50</td>
        <td
          className='text-center'
          onMouseEnter={() => setColumnHover(2)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 2),
            borderLeft: borderStyle(columnHover, currentHover, 2),
          }}
        >1,000</td>
        <td
          className='text-center'
          onMouseEnter={() => setColumnHover(3)}
          onMouseLeave={() => setColumnHover(null)}
          style={{
            borderRight: borderStyle(columnHover, currentHover, 3),
            borderLeft: borderStyle(columnHover, currentHover, 3),
          }}
        >1,000,000</td>
      </tr>
      {Features.map((feature, i) => (
        <tr key={feature}>
          <td>{feature}</td>
          <SubscriptionFeature
            feature={feature}
            borderStyle={borderStyle(columnHover, currentHover, 1)}
            setColumnHover={setColumnHover}
            featureGroup="Free"
            columnNumber={1}
          />
          <SubscriptionFeature
            feature={feature}
            borderStyle={borderStyle(columnHover, currentHover, 2)}
            setColumnHover={setColumnHover}
            featureGroup="Basic"
            columnNumber={2}
          />
          <SubscriptionFeature
            feature={feature}
            borderStyle={borderStyle(columnHover, currentHover, 3)}
            setColumnHover={setColumnHover}
            featureGroup="Premium"
            columnNumber={3}
          />
        </tr>
      ))}
      <tr>
        <td></td>
        {
          [SubscriptionFeatures.Free, SubscriptionFeatures.Basic, SubscriptionFeatures.Premium].map((subscription, i) => (
            <td
              key={i}
              className='text-center'
              onMouseEnter={() => setColumnHover(i + 1)}
              onMouseLeave={() => setColumnHover(null)}
              style={{
                borderRight: borderStyle(columnHover, currentHover, i + 1),
                borderLeft: borderStyle(columnHover, currentHover, i + 1),
                borderBottom: borderStyle(columnHover, currentHover, i + 1),
              }}
            >
              {!!subscription.maxPrice && <>
                <div className='mb-2'>Starting at ${formatIntegerCentAmount(subscription.minPrice)} / mo</div>
                <Button variant='outline-light' onClick={() => onSelect(subscription)}>Select</Button>
              </>}
            </td>
          ))
        }
      </tr>
    </tbody>
  </Table>
}



export const SubscriptionModal = ({
  closeModal,
  userInfo,
  displayFeatureRequiresMessage = false,
}: {
  userInfo: UserInfo;
  closeModal: () => void;
  displayFeatureRequiresMessage?: boolean;
}) => {
  const [showFeatureRequiresMessage, setShowFeatureRequiresMessage] = useState(displayFeatureRequiresMessage);
  const [selected, setSelected] = React.useState<SubscriptionType | null>(null);
  const [selectedPrice, setSelectedPrice] = React.useState<number | null>(null);
  const [fetchingPaymentIntent, setFetchingPaymentIntent] = useState(false);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] = useState<UserInfo>(userInfo);
  const [attemptedRefetchUser, setAttemptedRefetchUser] = useState(false);
  const { appendControls } = useRepToolContext();

  const createPrice = async () => {
    if (!selected || !selectedPrice) return;
    setFetchingPaymentIntent(true);
    let searchParams = new URLSearchParams({ api_key: localStorage.getItem("reptoolCookie") || '' });


    const createdPriceId = await fetch(`${(window as any).serverLocation}stripe/create_price?${searchParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        price: selectedPrice,
        subscription_type: selected.value
      })
    }).then(res => res.json()).then(({ priceId }) => priceId);

    setPriceId(createdPriceId);


    const customerResponse = await fetch(`${(window as any).serverLocation}stripe/create_customer?${searchParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
    }).then(res => res.json());

    const customerId = customerResponse.customer_id;

    // Step 2: Create subscription
    fetch(`${(window as any).serverLocation}stripe/create_subscription?${searchParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerId: customerId,
        priceId: createdPriceId,
        subscription_type: selected.value
      })
    }).then(res => res.json()).then(({ clientSecret }) => setClientSecret(clientSecret));
  }

  const refetchUserInfo = () => {
    // do a refetch just in case the user has verified their email
    BasicFetch({
      url: `${(window as any).serverLocation}/user/get_user`,
      method: 'GET',
      onSuccess: (data) => {
        if (data.message !== 'Bad token') {
          (window as any).currentUser = { ...data }
          setCurrentUserInfo({ ...data });
        }
      },
    });
  }

  useEffect(() => {
    appendControls(null);
  }, []);
  useEffect(() => {
    if(!userInfo.verified && !attemptedRefetchUser) {
      refetchUserInfo();
      setAttemptedRefetchUser(true);
    }
  }, [userInfo.verified, attemptedRefetchUser]);

  useEffect(() => {
    if (selected && !selectedPrice) {
      setSelectedPrice(selected.defaultPrice);
    } else if (!selected) {
      setSelectedPrice(null);
    }
  }, [selected, selectedPrice]);

  const appearance: any = {
    theme: 'night',
  };
  const loader = 'auto';

  const activeIndex = useMemo(() => {
    if (showFeatureRequiresMessage) return 0;
    if (!currentUserInfo.verified && !verificationEmailSent) return 1;
    if (!currentUserInfo.verified && verificationEmailSent) return 2;
    if (!selected) return 3;
    if (!clientSecret) return 4;
    else return 5;
  }, [selected, clientSecret, verificationEmailSent, showFeatureRequiresMessage, currentUserInfo.verified]);

  const title = useMemo(() => {
    if (showFeatureRequiresMessage) return "Feature Requires Subscription";
    if (!currentUserInfo.verified) return "Verify your email";
    if (!selected) return "Select a Subscription";
    if (!priceId) return "Select a Price";
    else return "Complete Payment";
  }, [selected, priceId, showFeatureRequiresMessage, currentUserInfo.verified]);

  return (
    <ReptoolModal
      id="subscription-modal"
      closeModal={closeModal}
      title={title}
      body={
        <div id='subscription-container' style={{ height: 'calc(95vh - 100px)', overflowY: 'auto' }}>
          <Carousel showThumbs={false} showStatus={false} showIndicators={false} showArrows={false} swipeable={false} selectedItem={activeIndex}>
            <FeatureRequiresSubscription clickFunction={() => setShowFeatureRequiresMessage(false)} />

            <div>
              <h4>Email Not Verified</h4>
              <p>You must verify your email before you can purchase a subscription. This ensures you will be able to easily cancel if needed.</p>
              <ResendVerificationEmailForm userEmail={currentUserInfo.email} onSend={() => setVerificationEmailSent(true)} />
            </div>

            <div>
              <h4>Verification Email Resent</h4>
              <p>An email has been sent to the specified email address. Please check your inbox and spam folder.
                If you do not receive the email within 5 minutes, please contact support.</p>
              <p>Once you have verified your email, click the button below and continue starting your subscription</p>
              <Button variant='outline-light' className='mt-3' onClick={() => (window as any).location.reload()}>Reload</Button>
            </div>
            <div>
              <SubscriptionTable onSelect={(subscription) => {
                // scroll to the top of the modal
                const modal = document.getElementById('subscription-container');
                if (modal) {
                  modal.scrollTo({
                    top: 0,
                  });
                }
                setSelected(subscription)
              }} />
            </div>
            {selected && selectedPrice ? <>
              <div className='d-flex'>
                <Button variant='outline-light' size='sm' className='mb-3' onClick={() => setSelected(null)}>
                  <FontAwesomeIcon icon={faArrowLeft} className='mr-2' /> Back
                </Button>
              </div>
              <SelectSubscriptionPrice
                minPrice={selected.minPrice}
                maxPrice={selected.maxPrice}
                setSelectedPrice={setSelectedPrice}
                selectedPrice={selectedPrice}
                selectedSubscription={selected}
                onContinue={createPrice}
                fetchingPaymentIntent={fetchingPaymentIntent}
              />
            </> : <></>}
            {selectedPrice && clientSecret ? <>
              <div className='d-flex'>
                <Button variant='outline-light' className='mb-3' size='sm' onClick={() => setClientSecret(null)}>
                  <FontAwesomeIcon icon={faArrowLeft} className='mr-2' /> Back
                </Button>
              </div>
              <h4>Payment</h4>
              <h6>Subscription: {selected?.name}</h6>
              <h6>Price: ${formatIntegerCentAmount(selectedPrice)}/mo</h6>

              <Elements options={{ clientSecret, appearance, loader }} stripe={(window as any).stripePromise}>
                <CheckoutForm />
              </Elements>
            </> : <></>}
          </Carousel>
        </div>
      }
    />
  )
}